import React from "react";
import styled from "styled-components";
import classNames from "classnames";
import { DocumentTypeIcon } from "./DocumentTypeIcon";
import { IconProp, SizeProp } from "@fortawesome/fontawesome-svg-core";
import { niceBytes } from "../../helpers/fileHelpers";
import { variantToColour } from "../../helpers/theme"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { OverlayTrigger, Popover } from "react-bootstrap";

export type DocumentDetailsCardProps = {
    bordered?: boolean,
    className?: any
    clearFile?: (() => void) | null | undefined
    clearIcon?: IconProp
    clearIconSize?: SizeProp
    clearTooltipText?: string
    clearTooltipTextAlt?: string
    fileName: string
    fileSize: number
    isClearable?: boolean
    isProtected?: boolean | null
    protectedFileIcon?: IconProp
    protectedFileIconAlt?: IconProp
    protectedFileIconClick?: (() => void) | null | undefined
    protectedFileTooltipText?: string
    protectedFileTooltipTextAlt?: string
    type: number
};

const StyledIcon = styled(FontAwesomeIcon)`
    color: ${(props: any) => variantToColour({ theme: props.theme, variant: props.variant })};
`;

export const DocumentDetailsCard = ({ 
    bordered = true,
    className,
    clearFile,
    clearIcon = ['fas', 'times'] as IconProp,
    clearIconSize = 'lg',
    clearTooltipText = "Remove file.",
    clearTooltipTextAlt = "File cannot be removed.",
    fileName, 
    fileSize, 
    isClearable = true,
    isProtected = null,
    protectedFileIcon = ['fas', 'lock'] as IconProp,
    protectedFileIconAlt = ['fas', 'lock-open'] as IconProp,
    protectedFileIconClick,
    protectedFileTooltipText = "This file attachment will be sent password protected by the clients default password.",
    protectedFileTooltipTextAlt = "This file attachment will be sent without password protection.",
    type 
} : Readonly<DocumentDetailsCardProps>) => (
    <div className={classNames("d-flex px-3 py-2", className, {
        'border border-3 border-opacity-10': bordered === true
    })}>
        <DocumentTypeIcon 
            icon={["fas", "file-word"]}
            documentType={type}
            outerElement={{
                className: "d-flex justify-content-center align-items-center"
            }}
        />
        <div className="flex-fill d-flex flex-column justify-content-center px-3">
            <p className="fs-6 m-0 text-truncate lh-1">
                <span>{fileName}</span>
                <br />
                <span className="text-muted fs-smallest">{niceBytes(fileSize)}</span>
            </p>
        </div>
        {clearFile && typeof(clearFile) === 'function' && (
            <div className="d-flex flex-column justify-content-center align-items-center">
                <div>
                    <OverlayTrigger
                        placement="auto"
                        overlay={(
                            <Popover>
                                <Popover.Body>
                                    {isClearable ? clearTooltipText : clearTooltipTextAlt}
                                </Popover.Body>
                            </Popover>
                        )}
                    >
                        <StyledIcon 
                            variant={isClearable ? 'danger' : 'muted'}
                            className={classNames({ 
                                'has-pointer': isClearable, 
                                'cursor-not-allowed opacity-30': !isClearable 
                            })} 
                            icon={clearIcon} 
                            size={clearIconSize} 
                            onClick={isClearable ? clearFile : () => {}} 
                        />
                    </OverlayTrigger>
                </div>
                {isProtected !== null && (
                    <div>
                        <OverlayTrigger
                            placement="auto"
                            overlay={(
                                <Popover>
                                    <Popover.Body>
                                        {isProtected === true ? protectedFileTooltipText : protectedFileTooltipTextAlt}
                                    </Popover.Body>
                                </Popover>
                            )}
                        >
                            <StyledIcon 
                                variant="muted"
                                className="has-pointer"
                                icon={isProtected === true ? protectedFileIcon : protectedFileIconAlt}
                                size={clearIconSize}
                                onClick={() => {
                                    if (protectedFileIconClick && typeof(protectedFileIconClick) === 'function') {
                                        protectedFileIconClick();
                                    }
                                }}
                            />
                        </OverlayTrigger>
                    </div>
                )}
            </div>
        )}
    </div>
)